import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { formatINR } from "@/lib/format";
import { getRange, rangeParams, rangeLabel } from "@/lib/period";
import PeriodFilter from "@/components/PeriodFilter";

const Card = ({ title, children, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={testid}>
    <h3 className="mb-4 font-heading font-bold text-slate-900">{title}</h3>
    {children}
  </div>
);

export default function Reports() {
  const { version } = useApp();
  const [periodKey, setPeriodKey] = useState("this_year");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [range, setRange] = useState(getRange("this_year"));
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/reports", { params: rangeParams(range) }).then((r) => setData(r.data)); }, [range, version]);

  if (!data) return <div className="p-8 text-slate-400">Loading reports…</div>;
  const p = data.pnl;
  const margin = p.profit_margin === null ? "N/A" : `${p.profit_margin.toFixed(2)}%`;

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Reports</h1>
          <p className="mt-1 text-slate-500">Period: <b className="text-slate-700">{rangeLabel(periodKey, range)}</b></p>
        </div>
        <PeriodFilter periodKey={periodKey} setPeriodKey={setPeriodKey} custom={custom} setCustom={setCustom} onChange={setRange} />
      </div>

      {/* P&L */}
      <Card title="Profit & Loss" testid="report-pnl">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div><div className="text-sm text-slate-500">Total Revenue</div><div className="font-heading text-xl font-bold text-emerald-600">{formatINR(p.total_revenue)}</div></div>
          <div><div className="text-sm text-slate-500">Total Expenses</div><div className="font-heading text-xl font-bold text-rose-600">{formatINR(p.total_expenses)}</div></div>
          <div><div className="text-sm text-slate-500">{p.net_profit < 0 ? "Net Loss" : "Net Profit"}</div><div className={`font-heading text-xl font-bold ${p.net_profit < 0 ? "text-rose-600" : "text-blue-600"}`}>{formatINR(Math.abs(p.net_profit))}</div></div>
          <div><div className="text-sm text-slate-500">Profit Margin</div><div className="font-heading text-xl font-bold text-purple-600">{margin}</div></div>
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Revenue Report" testid="report-revenue">
          <ReportTable
            head={["Revenue Type", "Txns", "Total", "% of Revenue"]}
            rows={data.revenue_report.map((r) => [r.name, r.count, formatINR(r.total), `${r.pct.toFixed(1)}%`])}
            empty="No revenue in this period"
          />
        </Card>
        <Card title="Expense Report" testid="report-expense">
          <ReportTable
            head={["Category", "Txns", "Total", "% of Expenses"]}
            rows={data.expense_report.map((r) => [r.name, r.count, formatINR(r.total), `${r.pct.toFixed(1)}%`])}
            empty="No expenses in this period"
          />
        </Card>
      </div>

      <Card title="Profit Allocation Report" testid="report-allocation">
        <ReportTable
          head={["Purpose", "Allocation %", "Allocated Amount"]}
          rows={data.allocation_report.map((r) => [r.name, `${r.percentage}%`, formatINR(r.amount)])}
          empty="No allocation configured"
          cols={3}
        />
      </Card>
    </div>
  );
}

function ReportTable({ head, rows, empty }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
            {head.map((h, i) => (<th key={i} className={`py-2 ${i > 0 ? "text-right" : ""}`}>{h}</th>))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={head.length} className="py-6 text-center text-slate-400">{empty}</td></tr>}
          {rows.map((r, ri) => (
            <tr key={ri} className="border-t border-slate-50">
              {r.map((c, ci) => (<td key={ci} className={`py-2.5 ${ci > 0 ? "text-right" : "text-slate-700"} ${ci === 0 ? "font-medium" : "text-slate-600"}`}>{c}</td>))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
